/**
 * Pagamentos na coleção `payments` (Firestore cliente): CRUD + consultas por usuário.
 */

import type { Firestore } from 'firebase/firestore';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';

import { getFirestoreDb } from '@/core/services';
import {
  normalizePaymentFields,
  Payment,
  PaymentRef,
  paymentSchema,
} from '@/core/domain';

export interface PaymentsRemoteDataSource {
  getById(id: string): Promise<Payment | null>;
  getAllByUserId(userId: string): Promise<Payment[]>;
  create(payment: Payment): Promise<Payment>;
  update(id: string, data: Partial<Payment>): Promise<Payment>;
  delete(id: string): Promise<void>;
  /**
   * Tempo real em `payments/{paymentId}`; valida `user_id` antes de emitir.
   */
  subscribeDocByIdForUser(
    paymentId: string,
    userId: string,
    onNext: (payment: Payment | null) => void,
    onError: (error: Error) => void,
  ): () => void;
}

function toPayment(id: string, data: Record<string, unknown>): Payment {
  const normalized = normalizePaymentFields(id, data);
  const parsed = paymentSchema.safeParse(normalized);
  if (!parsed.success) throw new Error('Invalid payment from Firestore');
  return parsed.data;
}

export class PaymentsFirestoreDataSource implements PaymentsRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  async getById(id: string): Promise<Payment | null> {
    const ref = doc(this.getDb(), PaymentRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toPayment(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAllByUserId(userId: string): Promise<Payment[]> {
    const q = query(
      collection(this.getDb(), PaymentRef.collection),
      where('user_id', '==', userId),
    );
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => toPayment(d.id, d.data() as Record<string, unknown>));
    return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  async create(payment: Payment): Promise<Payment> {
    const ref = doc(this.getDb(), PaymentRef.collection, payment.id);
    await setDoc(ref, payment);
    return payment;
  }

  async update(id: string, data: Partial<Payment>): Promise<Payment> {
    const ref = doc(this.getDb(), PaymentRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Payment not found');
    const current = toPayment(snap.id, snap.data() as Record<string, unknown>);
    const updated: Payment = {
      ...current,
      ...data,
      id,
      updated_at: new Date().toISOString(),
    };
    await setDoc(ref, updated, { merge: true });
    return updated;
  }

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(this.getDb(), PaymentRef.collection, id));
  }

  subscribeDocByIdForUser(
    paymentId: string,
    userId: string,
    onNext: (payment: Payment | null) => void,
    onError: (error: Error) => void,
  ): () => void {
    const ref = doc(this.getDb(), PaymentRef.collection, paymentId);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          onNext(null);
          return;
        }
        try {
          const p = toPayment(snap.id, snap.data() as Record<string, unknown>);
          if (p.user_id !== userId) {
            onError(new Error('Este pagamento não pertence à sua conta.'));
            return;
          }
          onNext(p);
        } catch {
          onError(new Error('Dados de pagamento inválidos.'));
        }
      },
      (err) => {
        onError(err instanceof Error ? err : new Error(String(err)));
      },
    );
  }
}

/** @deprecated Use `PaymentsFirestoreDataSource`. */
export const UserPaymentsFirestoreDataSource = PaymentsFirestoreDataSource;

/** @deprecated Use `PaymentsRemoteDataSource`. */
export type UserPaymentsRemoteDataSource = PaymentsRemoteDataSource;
